import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, ArrowLeft, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { meetingsAPI } from '../../lib/api';
import MeetingStatusBadge from './MeetingStatusBadge';
import ContactsEditor from './ContactsEditor';
import PointerQuestionsForm from './PointerQuestionsForm';
import MeetingFilesSection from './MeetingFilesSection';
import MeetingNotesSection from './MeetingNotesSection';
import MeetingActionItemsSection from './MeetingActionItemsSection';
import { fmtDateTimeInTz, isChinaHk, apiErrorText } from './meetingsUtils';

const VISIT_TYPE_LABELS = { WE_VISITED: 'At their office or site', THEY_VISITED: 'At our office', TRADE_SHOW: 'Trade show or event' };

export default function MeetingDetailView({ meetingId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [details, setDetails] = useState({});
  const [summary, setSummary] = useState('');
  const [summaryCn, setSummaryCn] = useState('');
  const [rating, setRating] = useState('');
  const [wechatGroup, setWechatGroup] = useState('');
  const [newContacts, setNewContacts] = useState([]);
  const [missing, setMissing] = useState([]);
  const [actionsRefreshTick, setActionsRefreshTick] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sendBackComment, setSendBackComment] = useState('');
  // Change G (26 Oct 2026, Lex's later round): emailing the full report to VSTL
  // attendees — ticked by default, individually selectable, never the company's contacts.
  const [sendReportEmail, setSendReportEmail] = useState(true);
  const [emailToUserIds, setEmailToUserIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, sRes] = await Promise.all([meetingsAPI.get(meetingId), meetingsAPI.getSettings()]);
      const m = mRes.data;
      setMeeting(m);
      setSettings(sRes.data);
      const amap = {}; const dmap = {};
      (m.answers || []).forEach((a) => { amap[a.question_code] = a.value; dmap[a.question_code] = a.detail; });
      setAnswers(amap); setDetails(dmap);
      setSummary(m.summary || ''); setSummaryCn(m.summary_cn || ''); setRating(m.relationship_rating || '');
      setWechatGroup(m.wechat_group_at_meeting || ''); setNewContacts([]); setMissing([]);
      setSendReportEmail(true);
      setEmailToUserIds([m.organiser_id, ...((m.attendee_users || []).map((u) => u.id))].filter((v, i, a) => a.indexOf(v) === i));
    } catch (e) {
      const status = e.response?.status;
      toast.error(status === 404 ? "You don't have access to this meeting" : status === 403 ? "You don't have access to Visits & Meetings" : 'Failed to load meeting');
      navigate('/meetings');
    } finally { setLoading(false); }
  }, [meetingId, navigate]);

  // Used after a file/note change -- refreshes only meeting.files/meeting.notes so an
  // in-progress, unsaved report draft (summary, answers, contacts...) is never wiped.
  const refreshMeetingOnly = async () => {
    try {
      const res = await meetingsAPI.get(meetingId);
      setMeeting(res.data);
    } catch { /* keep showing the last known meeting; the section itself already toasted */ }
  };

  useEffect(() => { load(); }, [load]);

  if (loading || !meeting) return <div className="flex justify-center py-12" data-testid="meeting-detail-loading"><Loader2 className="animate-spin" /></div>;

  const isOrganiser = meeting.organiser_id === user?.id;
  const isReviewer = meeting.reviewer_id === user?.id;
  const isParticipant = isOrganiser || (meeting.attendee_user_ids || []).includes(user?.id);
  const reportEditable = isParticipant && ['REPORT_DUE', 'SENT_BACK'].includes(meeting.status);
  const meetingEnded = new Date(meeting.start_at).getTime() + meeting.duration_min * 60000 <= Date.now();
  const showChinaHint = isChinaHk(meeting.company?.country_at_meeting);
  const canSendReport = isParticipant || isReviewer;

  // Union of answers+details keys so a note typed into an "Other" free-text box with no
  // option selected (e.g. competitors, which has no preset options) still gets saved.
  const toArray = (amap, dmap) => {
    const codes = new Set([...Object.keys(amap), ...Object.keys(dmap)]);
    return [...codes].map((code) => ({ code, value: amap[code], detail: dmap[code] }));
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await meetingsAPI.update(meeting.id, { summary, summary_cn: summaryCn, relationship_rating: rating ? Number(rating) : undefined,
        wechat_group_at_meeting: wechatGroup || undefined, answers: toArray(answers, details), contacts: newContacts });
      toast.success('Saved');
      setNewContacts([]);
      load();
    } catch (e) { toast.error(apiErrorText(e, 'Failed to save')); }
    finally { setSaving(false); }
  };

  const submitReport = async () => {
    setSaving(true); setMissing([]);
    try {
      await meetingsAPI.submit(meeting.id, { summary, summary_cn: summaryCn, relationship_rating: Number(rating),
        wechat_group_at_meeting: wechatGroup || undefined, answers: toArray(answers, details), contacts: newContacts,
        send_report_email: sendReportEmail,
        email_to_user_ids: sendReportEmail ? emailToUserIds : undefined,
      });
      toast.success('Report submitted for review');
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.missing) {
        setMissing(detail.missing); toast.error('Please complete the report before submitting');
        setActionsRefreshTick((t) => t + 1); // a failed submit may still have synced a new auto-draft server-side
      } else toast.error(apiErrorText(e, 'Failed to submit'));
    } finally { setSaving(false); }
  };

  const sendReportNow = () => doAction(() => meetingsAPI.sendReport(meeting.id, {}), 'Report emailed');

  const doAction = async (fn, successMsg) => {
    setSaving(true);
    try { await fn(); toast.success(successMsg); load(); }
    catch (e) { toast.error(apiErrorText(e, 'Action failed')); }
    finally { setSaving(false); }
  };

  const cancelMeeting = () => {
    const reason = window.prompt('Reason for cancelling this meeting:');
    if (!reason) return;
    let scope = 'this';
    if (meeting.series_id) {
      const wantsSeries = window.confirm(
        'This meeting is part of a repeating series.\n\n' +
        'Click OK to cancel THIS MEETING AND every later occurrence.\n' +
        'Click Cancel to choose only this meeting instead.'
      );
      if (wantsSeries) {
        scope = 'series';
      } else {
        const confirmThisOnly = window.confirm('Cancel only this meeting? Click OK to proceed, Cancel to not cancel anything.');
        if (!confirmThisOnly) return;
        scope = 'this';
      }
    }
    doAction(() => meetingsAPI.cancel(meeting.id, reason, scope), 'Meeting cancelled');
  };

  const joinMeeting = () => doAction(() => meetingsAPI.join(meeting.id), 'You joined this meeting');

  return (
    <div className="space-y-4" data-testid="meeting-detail-view">
      <Button variant="ghost" size="sm" onClick={() => navigate('/meetings')} data-testid="meeting-back-btn">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Meetings
      </Button>

      <Card className="rounded-sm">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-semibold">{meeting.company?.name_at_meeting}</h2>
              <p className="text-sm text-muted-foreground">
                {meeting.code} · {fmtDateTimeInTz(meeting.start_at, meeting.tz)} ({meeting.tz}) · {meeting.duration_min} min ·{' '}
                {meeting.mode === 'PHYSICAL' ? (VISIT_TYPE_LABELS[meeting.visit_type] || meeting.visit_type) : (meeting.platform || '').replace(/_/g, ' ')}
              </p>
              <p className="text-xs text-muted-foreground">Organiser: {meeting.organiser_name}</p>
              {(meeting.attendee_users?.length > 0 || meeting.attendee_visitors?.length > 0 || meeting.attendee_names_untagged?.length > 0) && (
                <p className="text-xs text-muted-foreground" data-testid="meeting-attendees-line">
                  Attended by: {[...(meeting.attendee_users || []).map((u) => u.name), ...(meeting.attendee_visitors || []).map((v) => v.name),
                    ...(meeting.attendee_names_untagged || []).map((n) => `${n} (typed, to tag)`)].join(', ')}
                </p>
              )}
              {(meeting.attendee_visitors || []).some((v) => v.reports_to_user_id) && (
                <p className="text-xs text-muted-foreground" data-testid="meeting-recorded-by-line">
                  Recorded by {meeting.organiser_name} on {fmtDateTimeInTz(meeting.created_at, meeting.tz)}
                </p>
              )}
            </div>
            <MeetingStatusBadge status={meeting.status} />
          </div>
          {meeting.cancel_reason && meeting.status === 'CANCELLED' && (
            <p className="text-sm text-muted-foreground" data-testid="meeting-cancel-reason">Cancelled: {meeting.cancel_reason}</p>
          )}
          {meeting.status === 'RESCHEDULED' && meeting.rescheduled_to_id && (
            <Button variant="link" size="sm" className="px-0" onClick={() => navigate(`/meetings/${meeting.rescheduled_to_id}`)} data-testid="meeting-view-rescheduled-btn">
              View the new meeting →
            </Button>
          )}
          {meeting.send_back_comment && meeting.status === 'SENT_BACK' && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-2" data-testid="meeting-send-back-comment">
              Sent back: {meeting.send_back_comment}
            </p>
          )}
          <div className="flex gap-2 flex-wrap pt-1">
            {isOrganiser && ['PLANNED', 'REPORT_DUE'].includes(meeting.status) && (
              <Button size="sm" variant="outline" onClick={cancelMeeting} data-testid="meeting-cancel-btn">Cancel meeting</Button>
            )}
            {!isParticipant && !['CANCELLED', 'RESCHEDULED'].includes(meeting.status) && (
              <Button size="sm" variant="outline" onClick={joinMeeting} data-testid="meeting-join-btn">Join this meeting</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {(reportEditable || meeting.status === 'REVIEWED' || meeting.status === 'SUBMITTED') && (
        <Card className="rounded-sm">
          <CardContent className="p-4 space-y-4">
            <h3 className="font-semibold">Report</h3>
            <ContactsEditor value={newContacts} onChange={setNewContacts} companyType={meeting.company.type} companyRef={meeting.company.ref}
              meetingId={meeting.id} attachedIds={meeting.contact_ids || []} disabled={!reportEditable} />
            {showChinaHint && (
              <div>
                <Label>WeChat group name</Label>
                <Input value={wechatGroup} onChange={(e) => setWechatGroup(e.target.value)} disabled={!reportEditable} data-testid="report-wechat-group-input" />
              </div>
            )}
            <div>
              <Label>Summary</Label>
              <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} disabled={!reportEditable} className="h-24" data-testid="report-summary-input" />
              <p className="text-xs text-muted-foreground">{summary.length} / {settings?.min_summary_chars || 80} min characters</p>
            </div>
            <div>
              <Label>Summary (Chinese, optional)</Label>
              <Textarea value={summaryCn} onChange={(e) => setSummaryCn(e.target.value)} disabled={!reportEditable} data-testid="report-summary-cn-input" />
            </div>
            <div className="w-48">
              <Label>Relationship rating (1-5)</Label>
              <Select value={String(rating || '')} onValueChange={(v) => setRating(v)} disabled={!reportEditable}>
                <SelectTrigger data-testid="report-rating-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {settings && (
              <PointerQuestionsForm questions={settings.questions} meeting={meeting} answers={answers} details={details}
                settings={settings} disabled={!reportEditable} onAnswer={(c, v) => reportEditable && setAnswers({ ...answers, [c]: v })}
                onDetail={(c, v) => reportEditable && setDetails({ ...details, [c]: v })} />
            )}
            {missing.length > 0 && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="submit-missing-fields">
                <p className="font-medium flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> Please complete before submitting:</p>
                <ul className="list-disc ml-5 mt-1">{missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
              </div>
            )}
            {reportEditable && (
              <div className="space-y-2 border-t pt-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="send-report-email-checkbox-label">
                  <input type="checkbox" checked={sendReportEmail} onChange={(e) => setSendReportEmail(e.target.checked)} data-testid="send-report-email-checkbox" />
                  Email this report to the attendees
                </label>
                {sendReportEmail && (
                  <div className="flex flex-wrap gap-2 pl-6">
                    {[{ id: meeting.organiser_id, name: `${meeting.organiser_name} (logger)` },
                      ...(meeting.attendee_users || []).filter((u) => u.id !== meeting.organiser_id)].map((p) => (
                      <label key={p.id} className="flex items-center gap-1.5 text-xs border rounded-sm px-2 py-1 cursor-pointer" data-testid={`email-recipient-${p.id}`}>
                        <input type="checkbox" checked={emailToUserIds.includes(p.id)}
                          onChange={() => setEmailToUserIds((ids) => ids.includes(p.id) ? ids.filter((i) => i !== p.id) : [...ids, p.id])} />
                        {p.name}
                      </label>
                    ))}
                    {(meeting.attendee_visitors || []).filter((v) => v.email).map((v) => (
                      <span key={v.id} className="text-xs border rounded-sm px-2 py-1 text-muted-foreground">{v.name} (field visitor)</span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {reportEditable && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={saving} onClick={saveDraft} data-testid="report-save-draft-btn">Save Draft</Button>
                <Button size="sm" disabled={saving || !meetingEnded} onClick={submitReport} data-testid="report-submit-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Submit Report
                </Button>
                {!meetingEnded && <p className="text-xs text-muted-foreground self-center">Can be submitted once the meeting ends.</p>}
              </div>
            )}
            {!reportEditable && canSendReport && ['SUBMITTED', 'SENT_BACK', 'REVIEWED'].includes(meeting.status) && (
              <Button size="sm" variant="outline" disabled={saving} onClick={sendReportNow} data-testid="send-report-now-btn">Send report</Button>
            )}
          </CardContent>
        </Card>
      )}

      {isReviewer && meeting.status === 'SUBMITTED' && (
        <Card className="rounded-sm">
          <CardContent className="p-4 space-y-2">
            <h3 className="font-semibold">Review this report</h3>
            <Textarea placeholder="Comment if sending back for changes" value={sendBackComment} onChange={(e) => setSendBackComment(e.target.value)} data-testid="review-send-back-comment-input" />
            <div className="flex gap-2">
              <Button size="sm" disabled={saving} onClick={() => doAction(() => meetingsAPI.review(meeting.id), 'Report reviewed')} data-testid="review-approve-btn">Mark Reviewed</Button>
              <Button size="sm" variant="outline" disabled={saving || !sendBackComment.trim()}
                onClick={() => doAction(() => meetingsAPI.sendBack(meeting.id, sendBackComment), 'Sent back for changes')} data-testid="review-send-back-btn">Send Back</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm"><CardContent className="p-4">
        <MeetingActionItemsSection meetingId={meeting.id} organiserId={meeting.organiser_id} organiserName={meeting.organiser_name}
          canAdd={isParticipant || isReviewer} actionItemError={missing.find((m) => m.toLowerCase().includes('action item'))}
          refreshSignal={actionsRefreshTick}
          onActionItemsChanged={() => setMissing((m) => m.filter((x) => !x.toLowerCase().includes('action item')))} />
      </CardContent></Card>

      <Card className="rounded-sm"><CardContent className="p-4">
        <MeetingFilesSection meetingId={meeting.id} files={meeting.files} onChanged={refreshMeetingOnly}
          canEdit={isParticipant && ['PLANNED', 'REPORT_DUE', 'SUBMITTED', 'SENT_BACK'].includes(meeting.status)}
          isVendorMeeting={meeting.company?.type === 'VENDOR'} />
      </CardContent></Card>

      <Card className="rounded-sm"><CardContent className="p-4">
        <MeetingNotesSection meetingId={meeting.id} notes={meeting.notes} onChanged={refreshMeetingOnly} />
      </CardContent></Card>
    </div>
  );
}
